import { createFocusTrap } from '@theme/utils';

class Header extends HTMLElement {
  constructor() {
    super();

    this.menuTrigger = this.querySelector('[js-site-header="trigger"]');
    this.mobileMenu = document.querySelector('[js-site-header="mobile-menu"]');
    this.progressRoot = this.querySelector('[js-site-header="progress"]');
    this.progressSteps = this.querySelectorAll('[js-site-header="progress-step"]');
    this.progressLines = this.querySelectorAll('[js-site-header="progress-line"]');
    this.mealPlanBuilder = null;
    this.isNavigatingProgressStep = false;
    this.toggleMobileMenu = this.toggleMobileMenu.bind(this);
    this.syncProgressFromBuilder = this.syncProgressFromBuilder.bind(this);
    this.handleProgressStepClick = this.handleProgressStepClick.bind(this);
    this.handleProgressStepKeyDown = this.handleProgressStepKeyDown.bind(this);

    if (this.mobileMenu) {
      this.focusTrap = createFocusTrap(this.mobileMenu);
    }
  }

  connectedCallback() {
    if (this.menuTrigger) {
      this.menuTrigger.addEventListener("click", this.toggleMobileMenu);
    }

    // Set initial state (closed by default)
    if (this.mobileMenu) {
      this.updateMenuTabindex(false);
    }

    if (this.progressRoot) {
      this.progressRoot.addEventListener('click', this.handleProgressStepClick);
      this.progressRoot.addEventListener('keydown', this.handleProgressStepKeyDown);
    }

    this.updateIrelandAccountPortalLinks();
    this.initializeProgressSync();
  }

  updateIrelandAccountPortalLinks() {
    const hostname = String(window.location.hostname || '').trim().toLowerCase();

    if (!hostname.includes('ie.simmereats')) {
      return;
    }

    const portalPath = '/tools/recurring/portal/9d4c1688be1a12fb5566ebc5906c88/overview';
    const irelandPortalLoginUrl = 'https://ie.simmereats.com/tools/recurring/login?redirect=customer_portal%2Foverview';

    this.querySelectorAll(`a[href="${portalPath}"]`).forEach((link) => {
      link.href = irelandPortalLoginUrl;
    });
  }

  disconnectedCallback() {
    if (this.menuTrigger) {
      this.menuTrigger.removeEventListener("click", this.toggleMobileMenu);
    }

    if (this.progressInitObserver) {
      this.progressInitObserver.disconnect();
      this.progressInitObserver = null;
    }

    if (this.progressStateObserver) {
      this.progressStateObserver.disconnect();
      this.progressStateObserver = null;
    }

    if (this.focusTrap) {
      this.focusTrap.disable();
    }

    if (this.progressRoot) {
      this.progressRoot.removeEventListener('click', this.handleProgressStepClick);
      this.progressRoot.removeEventListener('keydown', this.handleProgressStepKeyDown);
    }
  }

  initializeProgressSync() {
    if (!this.progressRoot || !this.progressSteps.length) {
      return;
    }

    this.applyProgressStep(1);

    const mealPlanBuilder = document.querySelector("meal-plan-builder");

    if (mealPlanBuilder) {
      this.attachProgressObserver(mealPlanBuilder);
      return;
    }

    this.progressInitObserver = new MutationObserver(() => {
      const mountedBuilder = document.querySelector("meal-plan-builder");

      if (!mountedBuilder) {
        return;
      }

      this.progressInitObserver.disconnect();
      this.progressInitObserver = null;
      this.attachProgressObserver(mountedBuilder);
    });

    this.progressInitObserver.observe(document.body, { childList: true, subtree: true });
  }

  attachProgressObserver(mealPlanBuilder) {
    this.mealPlanBuilder = mealPlanBuilder;

    if (this.progressStateObserver) {
      this.progressStateObserver.disconnect();
    }

    this.progressStateObserver = new MutationObserver(this.syncProgressFromBuilder);
    this.progressStateObserver.observe(this.mealPlanBuilder, {
      attributes: true,
      attributeFilter: ["data-step"]
    });

    this.syncProgressFromBuilder();
  }

  syncProgressFromBuilder() {
    const currentStep = this.getCurrentBuilderStep();
    this.applyProgressStep(currentStep);
  }

  getCurrentBuilderStep() {
    const parsedStep = Number(this.mealPlanBuilder?.dataset?.step);

    if (!Number.isFinite(parsedStep)) {
      return 1;
    }

    return Math.min(Math.max(Math.round(parsedStep), 1), 3);
  }

  isProgressStepClickable(stepIndex, currentStep = this.getCurrentBuilderStep()) {
    if (!this.mealPlanBuilder) {
      return false;
    }

    if (!Number.isFinite(stepIndex) || stepIndex < 1 || stepIndex > 3) {
      return false;
    }

    if (stepIndex > currentStep) {
      return false;
    }

    if (typeof this.mealPlanBuilder?.canNavigateToStepFromHeader === 'function') {
      return Boolean(this.mealPlanBuilder.canNavigateToStepFromHeader(stepIndex));
    }

    return true;
  }

  async navigateToProgressStep(stepIndex) {
    if (this.isNavigatingProgressStep || !this.mealPlanBuilder) {
      return;
    }

    if (!this.isProgressStepClickable(stepIndex)) {
      return;
    }

    this.isNavigatingProgressStep = true;

    try {
      if (typeof this.mealPlanBuilder?.navigateToStepFromHeader === 'function') {
        await this.mealPlanBuilder.navigateToStepFromHeader(stepIndex);
      } else if (typeof this.mealPlanBuilder?.goToStep === 'function') {
        await this.mealPlanBuilder.goToStep(stepIndex);
      }
    } finally {
      this.isNavigatingProgressStep = false;
      this.syncProgressFromBuilder();
    }
  }

  handleProgressStepClick(event) {
    const stepElement = event.target?.closest?.('[js-site-header="progress-step"]');

    if (!stepElement || !this.progressRoot?.contains(stepElement)) {
      return;
    }

    const stepIndex = Number(stepElement.dataset.stepIndex);

    if (!this.isProgressStepClickable(stepIndex)) {
      return;
    }

    event.preventDefault();
    void this.navigateToProgressStep(stepIndex);
  }

  handleProgressStepKeyDown(event) {
    const stepElement = event.target?.closest?.('[js-site-header="progress-step"]');

    if (!stepElement || !this.progressRoot?.contains(stepElement)) {
      return;
    }

    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    const stepIndex = Number(stepElement.dataset.stepIndex);

    if (!this.isProgressStepClickable(stepIndex)) {
      return;
    }

    event.preventDefault();
    void this.navigateToProgressStep(stepIndex);
  }

  applyProgressStep(step) {
    if (!this.progressRoot) {
      return;
    }

    this.progressRoot.dataset.currentStep = String(step);

    this.progressSteps.forEach((stepElement) => {
      const stepIndex = Number(stepElement.dataset.stepIndex);
      const isCheckoutStep = stepIndex === 4;

      let state = "pending";
      if (isCheckoutStep) {
        state = "disabled";
      } else if (stepIndex === step) {
        state = "active";
      } else if (stepIndex < step) {
        state = "complete";
      }

      stepElement.dataset.state = state;
      const isStepClickable = this.isProgressStepClickable(stepIndex, step);
      stepElement.dataset.clickable = String(isStepClickable);

      if (isStepClickable) {
        stepElement.setAttribute('role', 'button');
        stepElement.setAttribute('tabindex', '0');
        stepElement.setAttribute('aria-disabled', 'false');
      } else {
        stepElement.setAttribute('role', 'listitem');
        stepElement.setAttribute('tabindex', '-1');
        stepElement.setAttribute('aria-disabled', 'true');
      }

      const label = stepElement.querySelector('[js-site-header="progress-label"]');
      if (label) {
        if (state === "active") {
          label.setAttribute("aria-current", "step");
        } else {
          label.removeAttribute("aria-current");
        }
      }
    });

    this.progressLines.forEach((lineElement) => {
      const lineIndex = Number(lineElement.dataset.lineIndex);
      const isActive = lineIndex < 3 && lineIndex <= step;

      lineElement.dataset.active = String(isActive);
    });
  }

  toggleMobileMenu() {
    const isActive = this.mobileMenu.hasAttribute("data-active");
    this.mobileMenu.toggleAttribute("data-active");
    this.menuTrigger.toggleAttribute("data-active");

    // Update tabindex based on new state
    this.updateMenuTabindex(!isActive);

    // Enable/disable focus trap
    if (!isActive && this.mobileMenu) {
      this.focusTrap.enable();

      // When opening, focus first focusable element for accessibility
      const firstFocusable = this.mobileMenu.querySelector('a, button');
      if (firstFocusable) {
        setTimeout(() => {
          firstFocusable.focus();
        }, 0);
      }
    } else {
      this.focusTrap.disable();
    }
  }

  updateMenuTabindex(isOpen) {
    if (!this.mobileMenu) return;

    // Find all focusable elements inside the menu
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ');

    const focusableElements = this.mobileMenu.querySelectorAll(focusableSelectors);

    focusableElements.forEach(element => {
      if (isOpen) {
        // Restore natural tabindex when open
        if (element.dataset.savedTabindex !== undefined) {
          element.tabIndex = parseInt(element.dataset.savedTabindex, 10);
          delete element.dataset.savedTabindex;
        } else if (element.hasAttribute('tabindex')) {
          element.removeAttribute('tabindex');
        }
      } else {
        // Save current tabindex and set to -1 when closed
        if (element.hasAttribute('tabindex')) {
          element.dataset.savedTabindex = element.tabIndex;
        }
        element.tabIndex = -1;
      }
    });
  }
}



window.customElements.define("site-header", Header);
